<template>
    <section
        :id="id"
        class="s-slice s-side-menu"
        :class="{ ...className, ...basicClassName }"
    >
        <div class="a-container">
            <a-slice-header
                v-if="title || lead"
                :title="title"
                :lead="lead"
                :body-text="bodyText"
            />
            <div
                ref="leftsideEl"
                class="s-side-menu__leftside"
                :class="{
                    's-side-menu__leftside--sticky': sticky,
                    's-side-menu__leftside--sticky-active': stickyActive,
                }"
                :style="{ '--side-menu-mobile-top-offset': stickyMobileTopOffset + 'px' }"
            >
                <s-side-menu-control
                    :type="type"
                    :slices="slices"
                    :groups="groups"
                    :active-element="activeElement"
                    :style="{ top: `var(--side-menu-sticky-top-offset, ${stickyTopOffset}px)` }"
                    @tab-change="tabChange"
                />
                <div v-if="links" class="s-side-menu__links">
                    <a-link
                        v-for="(link, i) in links"
                        :key="i"
                        class="s-side-menu__link"
                        v-bind="link"
                        :to="link.link"
                        :class="link.class || ''"
                        :glyph-position="link.glyphPosition || 'left'"
                        :size="link.size || 'paragraph'"
                        @click="tabChange(link.id, link.text)"
                    />
                </div>
            </div>
            <div class="s-side-menu__rightside">
                <s-side-menu-content
                    ref="contentEl"
                    :type="type"
                    :slices="slices"
                    :active-element="activeElement"
                />
            </div>
        </div>
    </section>
</template>

<script setup>
import { throttle } from 'lodash-es';
import {
    ref,
    computed,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ALink from '@core/components/link/link.vue';
import ASliceHeader from '@core/components/slice-header/slice-header.vue';
import { contentProps } from '@core/composables/useContent';
import { styleProps, useStyle } from '@core/composables/useStyle';
import SSideMenuContent from './side-menu-content.vue';
import SSideMenuControl from './side-menu-control.vue';


defineOptions({ name: 'SSideMenu' });

const emit = defineEmits(['click']);

const props = defineProps({
    ...styleProps,
    ...contentProps,
    /**
     * Content slice data
     */
    slices: { type: Array, required: true },

    /**
     * Type of side menu
     */
    type: { type: String, default: 'tabs' },

    /**
     * Is wide layout
     */
    isWide: { type: Boolean, default: false },

    /**
     * Extra links after menu
     */
    links: { type: Array, default: undefined },

    /**
     * Title's lead text
     */
    lead: { type: String, required: false, default: undefined },

    /**
     * Title's body text
     */
    bodyText: { type: String, default: undefined },

    sticky: Boolean,

    /**
     * Used to pull scroll up in case there are other sticky elements on the page
     */
    scrollTopOffset: { type: Number, default: 0 },

    /**
     * Used to push sticky dropdown and menu down in case there are other sticky elements on the page
     */
    stickyTopOffset: { type: Number, default: 58 },

    stickyMobileTopOffset: { type: Number, default: 0 },

    isMobileCompact: Boolean,

    activeSlice: { type: String, default: '' },

    groups: { type: Array, default: () => [] },

    hasScroll: { type: Boolean, default: true },
});

const route = useRoute();
const router = useRouter();
const routeHash = computed(() => route?.hash ?? '');

const { className } = useStyle(props);

const basicClassName = computed(() => ({
    's-side-menu_layout_wide': props.isWide,
    's-side-menu_layout_narrow': !props.isWide,
    's-side-menu_layout_mobile-compact': props.isMobileCompact,
}));


function normalizeId(id) {
    return String(id || '').replace(/^#/, '').trim();
}

function getSliceId(slice) {
    return normalizeId(slice?.info?.id);
}

const contentEl = ref(null);
const leftsideEl = ref(null);
const isScrolled = ref(false);
const stickyActive = ref(false);
const activeElement = ref('');
let observer = null;
let scrollCb = null;

const tabIds = computed(() => props.slices.map(getSliceId).filter(Boolean));

function matchedTabHash() {
    const hash = normalizeId(routeHash.value);
    if (!hash) return '';
    return tabIds.value.includes(hash) ? hash : '';
}

watch(
    () => props.activeSlice,
    (value) => {
        const normalizedValue = normalizeId(value);
        if (normalizedValue) activeElement.value = normalizedValue;
    },
);

watch(routeHash, () => {
    const hash = matchedTabHash();

    if (props.type === 'tabs' && hash && hash !== activeElement.value) {
        checkTabAnchors({
            allowSameId: true,
            syncHash: false,
            shouldScroll: true,
            fallbackToDefault: false,
        });
    }
});


onMounted(() => {
    if (props.type === 'links' || props.sticky) {
        const hash = normalizeId(routeHash.value);
        activeElement.value = normalizeId(props.activeSlice) || hash || activeElement.value;
        if (!activeElement.value && !props.hasScroll && props.slices.length) {
            activeElement.value = getSliceId(props.slices[0]);
        }
        window.addEventListener('load', () => nextTick(registerObserver));
    }

    if (props.type === 'tabs') {
        const hasHash = Boolean(matchedTabHash());

        nextTick(() => {
            checkTabAnchors({
                allowSameId: true,
                syncHash: hasHash,
                shouldScroll: hasHash,
                fallbackToDefault: true,
            });
        });
    }

    if (props.type === 'combined' && props.slices.length) {
        nextTick(() => {
            activeElement.value = getSliceId(props.slices[0]);
        });
    }
});

onBeforeUnmount(() => {
    if (scrollCb) {
        window.removeEventListener('scroll', scrollCb);
    }

    if (observer) {
        observer.disconnect();
        observer = null;
    }

    window.removeEventListener('load', registerObserver);
});

function checkTabAnchors({allowSameId = false,
    syncHash = false,
    shouldScroll = false,
    fallbackToDefault = true} = {}) {
    if (!Array.isArray(props.slices) || !props.slices.length) return;

    const hash = matchedTabHash();

    const nextActive =
        normalizeId(props.activeSlice) ||
        hash ||
        (fallbackToDefault ? tabIds.value[0] : '');

    if (!nextActive) return;

    tabChange(nextActive, undefined, { allowSameId, syncHash, shouldScroll });
}

function resolveScrollTarget(id, options) {
    const targetId = options.scrollToId || id;
    const el = getSliceRefElement(targetId);
    if (el) return el;

    return contentEl.value?.$el ?? null;
}

function tabChange(id, name, options = {}) {
    const normalizedId = normalizeId(id);
    if (!normalizedId) return;

    const {allowSameId = false,
        syncHash = true,
        shouldScroll = false,
        scrollToId = null,
        syncScrollHash = false} = options;

    if (activeElement.value === normalizedId && !allowSameId && !props.activeSlice) {
        return;
    }

    activeElement.value = normalizedId;
    emit('click', normalizedId, name);

    const dropdownHeight = props.sticky ? 82 : 0;

    requestAnimationFrame(() => {
        const element = resolveScrollTarget(normalizedId, options);
        if (!element) return;

        const elCoords = getOffset(element);

        nextTick(() => {
            if (props.type === 'tabs' && syncHash) {
                const currentHash = normalizeId(routeHash.value);
                if (currentHash !== normalizedId) {
                    router?.replace({
                        path: route.path,
                        query: route.query,
                        hash: `#${normalizedId}`,
                    });
                }
            }

            if (props.type === 'combined' && scrollToId && syncScrollHash) {
                const currentHash = normalizeId(routeHash.value);
                if (currentHash !== normalizeId(scrollToId)) {
                    router?.replace({
                        path: route.path,
                        query: route.query,
                        hash: `#${normalizeId(scrollToId)}`,
                    });
                }
            }

            if (shouldScroll || isScrolled.value) {
                window.scrollTo({
                    top: elCoords.top - dropdownHeight - props.scrollTopOffset,
                });
            }
        });
    });
}

function getSliceRefElement(sliceId) {
    const maybe = contentEl.value?.$refs?.[sliceId];
    const target = Array.isArray(maybe) ? maybe[0] : maybe;
    return target?.$el || target;
}

function getOffset(el) {
    if (!el || typeof window === 'undefined') return { top: 0, left: 0 };

    const rect = el?.getBoundingClientRect?.();
    return {
        left: (rect?.left || 0) + window.scrollX,
        top: (rect?.top || 0) + window.scrollY,
    };
}

const handleScrollThrottled = throttle(handleScroll, 200);
function registerObserver() {
    if (typeof IntersectionObserver === 'undefined') return;

    if (observer) {
        observer.disconnect();
        observer = null;
    }

    scrollCb = handleScrollThrottled;

    observer = new IntersectionObserver((entries) => {
        const intersected = entries[0].isIntersecting;

        if (intersected) {
            window.addEventListener('scroll', scrollCb);
        } else {
            window.removeEventListener('scroll', scrollCb);
        }
    });

    const el = contentEl.value?.$el;
    if (el) observer.observe(el);
}

function handleScroll() {
    if (props.sticky) {
        const stickyElement = leftsideEl.value;
        if (!stickyElement || !stickyElement.offsetParent) return;

        const offsetParent = stickyElement.offsetParent.offsetTop;
        const positionedElement = window.innerWidth < 1024
            ? stickyElement
            : stickyElement.querySelector('.s-side-menu-control');
        const topOffset = positionedElement
            ? parseFloat(getComputedStyle(positionedElement).top) || 0
            : props.stickyTopOffset;
        const stickyTop = stickyElement.offsetTop + offsetParent - topOffset;

        stickyActive.value = window.pageYOffset >= stickyTop;
        isScrolled.value = window.pageYOffset >= stickyTop;
    }

    if (!props.hasScroll) return;

    const SCROLL_OFFSET = 250;

    requestAnimationFrame(() => {
        const entries = Object.entries(contentEl.value?.$refs || {});

        const found = entries.find(([key, value]) => {
            if (!key) return false;

            const maybeComp = Array.isArray(value) ? value[0] : value;
            const el = maybeComp?.$el || maybeComp;

            const rect = el?.getBoundingClientRect?.();
            return rect?.bottom > SCROLL_OFFSET;
        });

        if (found) activeElement.value = found[0];
    });
}
</script>

<style lang="pcss">
.s-side-menu {
  &_layout {
    &_wide {
      .s-side-menu {
        &__leftside {
          width: 100%;

          @media (--viewport-mobile-wide) {
            .s-side-menu-control {
              width: 50%;
            }
          }

          @media (--viewport-desktop) {
            .s-side-menu-control {
              width: 100%;
              top: 24px;
            }
            width: 33.33%;
          }

          @media (--viewport-desktop-wide) {
            width: 25%;
          }
        }

        &__rightside {
          @media (--viewport-desktop) {
            width: 66.66%;
          }

          @media (--viewport-desktop-wide) {
            width: 75%;
          }
        }
      }
    }

    &_narrow {
      .s-side-menu {
        &__leftside {
          @media (--viewport-desktop) {
            width: 25%;
          }

          @media (--viewport-desktop-wide) {
            width: 16.66%;
          }
        }

        &__rightside {
          @media (--viewport-desktop) {
            width: 75%;
          }

          @media (--viewport-desktop-wide) {
            width: 83.34%;
          }
        }
      }
    }

    &_mobile-compact {
      .a-container {
        .a-slice-header {
          margin-bottom: 40px;
        }
      }
      .s-side-menu {
        &__leftside {
          margin-bottom: -16px;
        }
      }
    }
  }

  .a-slice-header {
    width: 100%;
    margin-bottom: 56px;

    @media (--viewport-desktop) {
      &__lead,
      &__title {
        max-width: 635px;
      }
    }

    @media (--viewport-desktop-wide) {
      &__lead,
      &__title {
        max-width: 763px;
      }
    }

    @media (--viewport-desktop-large) {
      &__lead,
      &__title {
        max-width: 869px;
      }
    }
  }

  & > .a-container {
    @media (--viewport-desktop) {
      display: flex;
      flex-wrap: wrap;
    }
  }

  &__leftside {
    top: var(--side-menu-mobile-top-offset, 0px);
    width: 100%;
    padding-inline-end: 16px;
    padding: 0 0 16px;
    inset-inline-start: 0;

    &--sticky {
      top: var(--anchor-menu-sticky-bottom, var(--side-menu-mobile-top-offset, 0px));
      position: sticky;
      z-index: 10;

      @media only screen and (max-width: 1023px) {
        background: var(--av-inversed-primary);
        position: sticky;
        width: calc(100% + 32px) !important;
        margin: 0 -32px;
        padding: 16px 32px;
        inset-inline-start: 0;

        @media (--viewport-mobile-wide) {
          width: calc(100% + 64px) !important;
          margin: 0 -64px;
        }

        @media (--viewport-desktop) {
          margin: 0;
        }

        .s-side-menu-control {
          width: 100%;

          @media (--viewport-tablet) {
            width: 50%;
          }
        }
      }
    }

    &--sticky-active {
      background-color: white;
      box-shadow: 0 7px 7px var(--av-fixed-invisible);
    }

    @media (--viewport-desktop) {
      padding-inline-end: 32px;
      box-shadow: none;

      &--sticky {
        position: static;

        .s-side-menu-control {
          width: unset;
          position: sticky;
        }
      }
    }
  }

  &__links {
    padding-top: 24px;

    @media (--viewport-desktop) {
      border-top: 1px solid var(--av-brand-secondary-light);
      margin-top: 36px;
    }
  }

  &__link {
    display: block;
    padding-inline-start: 16px;
    margin-bottom: 16px;

    .a-link__glyph {
      width: 16px;
      float: left;
      margin-inline-end: 8px;
      margin-inline-start: 0;
      position: relative;
      top: 6px;
    }
  }

  .s-video-list {
    .a-slice-header {
      &__title {
        font-size: 24px;

        @media (--viewport-tablet) {
          font-size: 32px;
        }
      }

      &__lead {
        font-size: 16px;
      }
    }

    &__item {
      @media (--viewport-tablet) {
        padding: 0 8px;
        width: 50%;
      }

      @media (--viewport-desktop) {
        width: 50%;
      }

      @media (--viewport-desktop-wide) {
        width: 33.33%;
      }
    }
  }
}

.el-select-dropdown.el-popper {
  background: var(--av-inversed-primary);
}
</style>
